import React, { createRef, FunctionComponent, useEffect, useState } from "react";
import styles from "./BannerCurvedMedia.module.scss";
import BannerCurvedMediaContent from "./BannerCurvedMediaContent.model";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import formatDynamicTextColor from "blocks/helpers/formatDynamicTextColor";
import getImageCrop from "app/media/helpers/getImageCrop";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import getProcessedRichtext from "blocks/helpers/getProcessedRichtext";
import getWrapperStyles from "blocks/helpers/getWrapperStyles";
import IconViewer from "components/IconViewer/IconViewer";
import ImageViewer from "components/ImageViewer/ImageViewer";
import useWindowDimensions from "hooks/useWindowDimensions";
import formatDynamicTextBreak from "blocks/helpers/formatDynamicTextBreak";
import VideoViewer from "components/VideoViewer/VideoViewer";
import getVideoHeightRatio from "app/media/helpers/getVideoHeightRatio";
import BlockButton from "components/BlockButton/BlockButton";
import SimpleImage from "app/blocks/SimpleImage/SimpleImage";

const MediaRenderer = ({ media, imageCrop, imageAspectRatio, videoHeightRatio }: any) => {
    if (!media) {
        console.warn("[MediaRenderer] No media found");
        return null;
    }
    const mediaUrl = (typeof media === "string" ? media : media?.url) || "";
    const fileExtension = mediaUrl.split(".").pop()?.toLowerCase() || "";
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"];
    const videoExtensions = ["mp4", "webm", "mov", "avi", "mkv", "ogg"];

    const isImage = imageExtensions.includes(fileExtension);
    const isVideo = videoExtensions.includes(fileExtension);
    const videoSrc = mediaUrl;

    if (isImage) {
        return (
            <ImageViewer
                className={styles.image}
                src={media || media?.url}
                crop={imageCrop}
                focalPoint={media.focalPoint}
                {...imageAspectRatio}
                alt=""
            />
        );
    }

    if (isVideo && videoSrc) {
        // console.log("[MediaRenderer] Rendering VideoViewer with src:", videoSrc);
        return (
            <VideoViewer
                className={styles.video}
                key={media._id}
                src={videoSrc}
                heightRatio={videoHeightRatio}
                alt=""
                autoplay
                loop
                soundButton={false}
            />
        );
    }

    console.warn("[MediaRenderer] No valid src for media:", media);
    return null;
};

const BannerCurvedMedia: FunctionComponent<BannerCurvedMediaContent> = (props) => {
    const processedHeadline = getProcessedRichtext(formatDynamicTextBreak(formatDynamicTextColor(props.content.headline)));
    const processedTagline = getProcessedRichtext(formatDynamicTextBreak(props.content.tagline));
    const wrapperStyles = getWrapperStyles(props.settings);

    const bgColor = props.settings?.backgroundColor?.color || "#d8e8c5";
    const buttonHoverBackgroundColor = props.settings?.buttonHoverBackgroundColor?.color || "";
    const buttonHoverTextColor = props.settings?.buttonHoverColor?.color || "";
    const maskColor = props.settings?.maskColor?.color || "#FFFFFF";

    const { width } = useWindowDimensions();
    const { mediaFirst, mediaSecond } = props.content;

    const imageCrop = getImageCrop(mediaFirst?.crops, undefined, width > 768 ? "Square (1:1)" : "Widescreen (16:9)");
    const imageAspectRatio = getImageAspectRatio(imageCrop, mediaFirst?.width, mediaFirst?.height);

    const videoHeightRatio = getVideoHeightRatio("Widescreen (16:9)");
    const wrapperElementRef = createRef<HTMLDivElement>();

    const clickScrollHandler = () => {
        if (wrapperElementRef.current) {
            const scrollOverThreshold = wrapperElementRef.current.scrollTop + wrapperElementRef.current.offsetHeight + (8 - 60);
            // console.log("[BannerCurvedMedia] scroll to:", scrollOverThreshold);
            window.scrollTo({
                top: scrollOverThreshold,
                behavior: "smooth",
            });
        }
    };

    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 0) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <div
            className={combineClasses([...wrapperStyles, scrolled ? styles.is_scrolled : ""])}
            style={
                {
                    "--btn-hover-bg": `${buttonHoverBackgroundColor}`,
                    "--btn-hover-color": `${buttonHoverTextColor}`,
                    backgroundColor: maskColor,
                } as React.CSSProperties
            }
        >
            <div
                ref={wrapperElementRef}
                className={styles.container}
                style={{ backgroundColor: `${bgColor}` }}
            >
                <div className={styles.inner}>
                    <div className={styles.mediaContainer}>
                        <MediaRenderer
                            media={mediaFirst?.url || mediaFirst}
                            imageCrop={imageCrop}
                            imageAspectRatio={imageAspectRatio}
                            videoHeightRatio={videoHeightRatio}
                        />
                    </div>

                    {/*  Content */}
                    <div className={styles.right_section}>
                        {props.content.showGraphicElement ? (
                            <div className={styles.content}>
                                {/* <IconViewer
                                    className={styles.icon}
                                    name="bagerrigt"
                                /> */}

                                <SimpleImage
                                    className={styles.img}
                                    src={props.content.graphicELement}
                                />
                            </div>
                        ) : (
                            <div className={styles.content}>
                                <div className={styles.contentInnerWrapper}>
                                    <span
                                        className={styles.tagline}
                                        dangerouslySetInnerHTML={processedTagline}
                                    />
                                    <DynamicHeading
                                        className={styles.headline}
                                        headlineSize={props.content.headlineSize}
                                        headlineWeight={props.content.headlineWeight}
                                        innerHtml={processedHeadline}
                                    />
                                    {props.content.buttons && props.content.buttons?.length > 0 && (
                                        <div className={styles.buttons}>
                                            {props.content.buttons.map((button) => (
                                                <BlockButton
                                                    key={button.contentUdi}
                                                    label={button?.label}
                                                    link={button?.link}
                                                    target={button?.target}
                                                    backgroundColor={button?.backgroundColor}
                                                    borderColor={button?.borderColor}
                                                    textColor={button?.textColor}
                                                    backgroundHoverColor={button?.backgroundHoverColor}
                                                    hoverTextColor={button?.hoverTextColor}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* Scroll Button */}
            <div
                className={styles.scrollIcon}
                onClick={clickScrollHandler}
            >
                <IconViewer
                    name="small-arrow-down"
                    heightRatio={31.552 / 10.848}
                />
            </div>
            <div className={styles.shape}></div>
        </div>
    );
};

export default BannerCurvedMedia;
